export const name = "NET.REG_DOMAIN";
export const summary = `Gets the registered or registrable domain from a URL.`;
export const reference = `
## NET.REG_DOMAIN

Gets the registered or registrable domain from a URL.

\`\`\`
NET.REG_DOMAIN(url)
\`\`\`

### Description

Takes a URL as a string and returns the registered or registrable domain (the public suffix plus one preceding label), as a string. For best results, URL values should comply with the format as defined by RFC 3986. If the URL value doesn't comply with RFC 3986 formatting, this function makes a best effort to parse the input and return a relevant result.

This function returns \`NULL\` if any of the following is true:

- It can't parse the host from the input;
- The parsed host contains adjacent dots in the middle (not leading or trailing);
- The parsed host doesn't contain any public suffix;
- The parsed host contains only a public suffix without any preceding label.

Before looking up the public suffix, this function temporarily normalizes the host by converting uppercase English letters to lowercase and encoding all non-ASCII characters with Punycode. The function then returns the registered or registerable domain as part of the original host instead of the normalized host.


### Note: The function doesn't perform
Unicode normalization.




### Note: The public suffix data at
publicsuffix.org also contains
private domains. This function doesn't treat a private domain as a public
suffix. For example, if us.com is a private domain in the public suffix data,
NET.REG_DOMAIN("foo.us.com") returns us.com (the public suffix com plus
the preceding label us) rather than foo.us.com (the private domain us.com
plus the preceding label foo).




### Note: The public suffix data may change over time.
Consequently, input that produces a NULL result now may produce a non-NULL
value in the future.




### Return Data Type

\`STRING\`


### Example



\`\`\`
SELECT
  FORMAT("%T", input) AS input,
  description,
  FORMAT("%T", NET.HOST(input)) AS host,
  FORMAT("%T", NET.PUBLIC_SUFFIX(input)) AS suffix,
  FORMAT("%T", NET.REG_DOMAIN(input)) AS domain
FROM (
  SELECT "" AS input, "invalid input" AS description
  UNION ALL SELECT "http://abc.xyz", "standard URL"
  UNION ALL SELECT "//user:password@a.b:80/path?query",
                   "standard URL with relative scheme, port, path and query, but no public suffix"
  UNION ALL SELECT "https://[::1]:80", "standard URL with IPv6 host"
  UNION ALL SELECT "http://例子.卷筒纸.中国", "standard URL with internationalized domain name"
  UNION ALL SELECT " www.Example.Co.UK ",
                   "non-standard URL with spaces, upper case letters, and without scheme"
  UNION ALL SELECT "mailto:?to=&subject=&body=", "URI rather than URL--unsupported"
);
\`\`\`

| input | description | host | suffix | domain |
| --- | --- | --- | --- | --- |
| "" | invalid input | NULL | NULL | NULL |
| "http://abc.xyz" | standard URL | "abc.xyz" | "xyz" | "abc.xyz" |
| "//user:password@a.b:80/path?query" | standard URL with relative scheme, port, path and query, but no public suffix | "a.b" | NULL | NULL |
| "https://[::1]:80" | standard URL with IPv6 host | "[::1]" | NULL | NULL |
| "http://例子.卷筒纸.中国" | standard URL with internationalized domain name | "例子.卷筒纸.中国" | "中国" | "卷筒纸.中国" |
| "&nbsp;&nbsp;&nbsp;&nbsp;www.Example.Co.UK&nbsp;&nbsp;&nbsp;&nbsp;" | non-standard URL with spaces, upper case letters, and without scheme | "www.Example.Co.UK" | "Co.UK" | "Example.Co.UK" |
| "mailto:?to=&subject=&body=" | URI rather than URL--unsupported | "mailto" | NULL | NULL |


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/net_functions
`;
